import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination } from '@/components/Pagination';
import { CalendarDays, Search, X, User } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const PAGE_SIZE = 15;

export default function EmbassyAppointments() {
  const { t } = useI18n();
  const [appointments, setAppointments] = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(1);

  const load = () => {
    const params = dateFilter ? `?date=${dateFilter}` : '';
    api.get(`/appointments/embassy${params}`).then(r => setAppointments(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [dateFilter]);

  const handleCancel = async (apptId) => {
    if (!window.confirm('Annuler ce rendez-vous ?')) return;
    try {
      await api.post(`/appointments/${apptId}/cancel`);
      toast.success('Rendez-vous annule');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const paginated = appointments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="embassy-appointments-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('appt.title')}</h1>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFilter} onChange={e => { setDateFilter(e.target.value); setPage(1); }} className="w-44" data-testid="date-filter" />
          {dateFilter && (
            <Button variant="ghost" size="sm" onClick={() => setDateFilter('')}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <Card className="border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('appt.citizen')}</TableHead>
                <TableHead>{t('common.date')}</TableHead>
                <TableHead>{t('appt.time')}</TableHead>
                <TableHead>{t('appt.reason')}</TableHead>
                <TableHead>{t('card.status')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map(appt => (
                <TableRow key={appt.appointment_id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{appt.user_name}</p>
                        <p className="text-xs text-muted-foreground">{appt.user_email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{new Date(appt.date).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell className="text-sm font-mono font-medium">{appt.time}</TableCell>
                  <TableCell className="text-sm">{appt.reason}</TableCell>
                  <TableCell>
                    <Badge className={appt.status === 'confirmed' ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                      {appt.status === 'confirmed' ? t('appt.confirmed') : t('appt.cancelled')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {appt.status === 'confirmed' && (
                      <Button variant="ghost" size="sm" onClick={() => handleCancel(appt.appointment_id)} data-testid={`cancel-appt-${appt.appointment_id}`}>
                        <X className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {appointments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {t('appt.no_appointments')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <Pagination total={appointments.length} pageSize={PAGE_SIZE} currentPage={page} onPageChange={setPage} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
